import React, { useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  makeStyles,
  MenuItem,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";
import {
  CloudUpload,
  Mic,
  Stop,
  DeleteOutline
} from "@material-ui/icons";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  field: { marginTop: theme.spacing(1) },
  preview: {
    marginTop: theme.spacing(1),
    maxWidth: "100%",
    maxHeight: 180,
    borderRadius: 4,
    background: "#000"
  },
  row: {
    display: "flex",
    gap: 8,
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center"
  },
  hint: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: 11
  }
}));

const publicMediaUrl = (mediaUrl, mediaPath) => {
  if (!mediaUrl && !mediaPath) return "";
  if (mediaUrl && /^https?:\/\//i.test(mediaUrl)) return mediaUrl;
  const rel = (mediaUrl || `chatFlows/${mediaPath}`).replace(/^\//, "");
  const base = getBackendUrl() || "";
  return `${base.replace(/\/$/, "")}/public/${rel}`;
};

const MessageMediaConfig = ({ data, patch, lockedMediaType }) => {
  const classes = useStyles();
  const fileRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);

  const mediaType = lockedMediaType || data.mediaType || "text";

  const uploadBlob = async (fileOrBlob, fileName) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("media", fileOrBlob, fileName);
      const { data: res } = await api.post("/chat-flows/media", formData);
      patch({
        mediaType: lockedMediaType || res.mediaType || mediaType,
        mediaPath: res.mediaPath,
        mediaUrl: res.mediaUrl,
        fileName: res.fileName || fileName
      });
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = async e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    // Soft client-side size checks (Meta Cloud practical limits)
    const maxMb = mediaType === "video" ? 64 : mediaType === "audio" ? 16 : 5;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(i18n.t("chatFlows.editor.fileTooLarge", { max: maxMb }));
      return;
    }
    await uploadBlob(file, file.name);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
        ? "audio/ogg;codecs=opus"
        : "";
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = ev => {
        if (ev.data && ev.data.size) chunksRef.current.push(ev.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blobType = recorder.mimeType || "audio/webm";
        const ext = blobType.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        if (blob.size < 500) return;
        await uploadBlob(blob, `recording-${Date.now()}.${ext}`);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (err) {
      toastError(err);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    setRecording(false);
  };

  const clearMedia = () => {
    patch({ mediaPath: "", mediaUrl: "", fileName: "" });
  };

  const preview = publicMediaUrl(data.mediaUrl, data.mediaPath);

  return (
    <>
      {!lockedMediaType && (
        <TextField
          className={classes.field}
          select
          label={i18n.t("chatFlows.editor.mediaType")}
          value={mediaType}
          onChange={e => {
            const value = e.target.value;
            patch({
              mediaType: value,
              ...(value === "text"
                ? { mediaPath: "", mediaUrl: "", fileName: "" }
                : {})
            });
          }}
          variant="outlined"
          size="small"
          fullWidth
        >
          <MenuItem value="text">{i18n.t("chatFlows.editor.mediaText")}</MenuItem>
          <MenuItem value="image">{i18n.t("chatFlows.editor.mediaImage")}</MenuItem>
          <MenuItem value="video">{i18n.t("chatFlows.editor.mediaVideo")}</MenuItem>
          <MenuItem value="audio">{i18n.t("chatFlows.editor.mediaAudio")}</MenuItem>
        </TextField>
      )}

      {mediaType === "text" && (
        <TextField
          className={classes.field}
          label={i18n.t("chatFlows.editor.text")}
          value={data.text || ""}
          onChange={e => {
            const value = e.target.value;
            patch({ text: value });
          }}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
        />
      )}

      {(mediaType === "image" || mediaType === "video") && (
        <>
          <TextField
            className={classes.field}
            label={i18n.t("chatFlows.editor.caption")}
            value={data.text || ""}
            onChange={e => {
              const value = e.target.value;
              patch({ text: value });
            }}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <Typography className={classes.hint}>
            {mediaType === "video"
              ? i18n.t("chatFlows.editor.videoHint")
              : i18n.t("chatFlows.editor.imageHint")}
          </Typography>
        </>
      )}

      {mediaType === "audio" && (
        <>
          <Typography className={classes.hint}>
            {i18n.t("chatFlows.editor.audioHint")}
          </Typography>
          <FormControlLabel
            className={classes.field}
            control={
              <Switch
                color="primary"
                checked={data.asVoiceNote !== false}
                onChange={e => patch({ asVoiceNote: e.target.checked })}
              />
            }
            label={i18n.t("chatFlows.editor.asVoiceNote")}
          />
        </>
      )}

      {(mediaType === "image" ||
        mediaType === "video" ||
        mediaType === "audio") && (
        <TextField
          className={classes.field}
          label={i18n.t("chatFlows.editor.mediaUrl")}
          value={data.mediaUrl || ""}
          onChange={e => {
            const value = e.target.value;
            patch({ mediaUrl: value, mediaPath: value ? data.mediaPath : "" });
          }}
          variant="outlined"
          size="small"
          fullWidth
          helperText={i18n.t("chatFlows.editor.mediaUrlHint")}
        />
      )}

      {mediaType !== "text" && (
        <>
          <div className={classes.row}>
            <input
              ref={fileRef}
              type="file"
              hidden
              accept={
                mediaType === "image"
                  ? "image/*"
                  : mediaType === "video"
                  ? "video/mp4,video/quicktime,video/*"
                  : "audio/*,audio/ogg,audio/mpeg,audio/mp4,.ogg,.mp3,.m4a,.webm"
              }
              onChange={onFileChange}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={
                uploading ? <CircularProgress size={14} /> : <CloudUpload />
              }
              disabled={uploading || recording}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              {i18n.t("chatFlows.editor.upload")}
            </Button>
            {mediaType === "audio" && !recording && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<Mic />}
                disabled={uploading}
                onClick={startRecording}
              >
                {i18n.t("chatFlows.editor.record")}
              </Button>
            )}
            {mediaType === "audio" && recording && (
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<Stop />}
                onClick={stopRecording}
              >
                {i18n.t("chatFlows.editor.stopRecord")}
              </Button>
            )}
            {(data.mediaPath || data.mediaUrl) && (
              <Button
                size="small"
                startIcon={<DeleteOutline />}
                onClick={clearMedia}
              >
                {i18n.t("chatFlows.editor.clearMedia")}
              </Button>
            )}
          </div>
          {data.fileName && (
            <Typography className={classes.hint}>{data.fileName}</Typography>
          )}
          {preview && mediaType === "image" && (
            <img src={preview} alt="" className={classes.preview} />
          )}
          {preview && mediaType === "video" && (
            <video src={preview} controls className={classes.preview} />
          )}
          {preview && mediaType === "audio" && (
            <audio
              src={preview}
              controls
              style={{ width: "100%", marginTop: 8 }}
            />
          )}
        </>
      )}
    </>
  );
};

export default MessageMediaConfig;
